import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { AE_SECTION_TITLE } from '@/components/assessment-engine/layout/aeTheme';
import { GapValue } from '@/components/assessment-engine/GapValue';
import {
  formatScore,
  resolveItemScaleMax,
  scaleSigma,
  scoreGapFromTarget,
} from '@/components/assessment-engine/resultsScoreUtils';
import type { AeQuestionScoreItem } from '@/types/assessmentEngine';

interface QuestionScoresTableProps {
  questions: AeQuestionScoreItem[];
  scaleMax: number;
  showBenchmark?: boolean;
  footnote?: string;
  /** Override for the primary score column (admin: campaign average). */
  scoreColumnLabel?: string;
}

/**
 * Full numbered question score table companion to the spider / section rollup.
 * Each matrix statement appears as its own numbered scored question.
 * Gap-to-benchmark only when B1 is available (no gap-to-max column).
 */
export const QuestionScoresTable: React.FC<QuestionScoresTableProps> = ({
  questions,
  scaleMax,
  showBenchmark = false,
  footnote,
  scoreColumnLabel,
}) => {
  const { t } = useLanguage();
  const scoreLabel = scoreColumnLabel ?? t('assessmentEngine.results.yourScore');

  if (!questions.length) return null;

  return (
    <div className="mt-6 min-w-0 overflow-x-auto">
      <h2 className={`${AE_SECTION_TITLE} mb-3`}>
        {t('assessmentEngine.results.questionScores')}
      </h2>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--color-border)] text-left text-[var(--color-surface-text)]">
            <th className="py-2 pr-2 w-14">{t('assessmentEngine.results.questionNumber')}</th>
            <th className="py-2 pr-2">{t('assessmentEngine.report.section')}</th>
            <th className="py-2 pr-2">{t('assessmentEngine.results.question')}</th>
            <th className="py-2 pr-2">{scoreLabel}</th>
            {showBenchmark && (
              <th className="py-2 pr-2">{t('assessmentEngine.report.benchmark')}</th>
            )}
            {showBenchmark && (
              <th className="py-2">{t('assessmentEngine.results.gapToBenchmark')}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {questions.map((q) => {
            const label = q.statement_label?.trim()
              ? q.statement_label
              : q.question_text;
            const scoreDisplay = q.is_na ? 'N/A' : formatScore(q.score);
            const itemMax = resolveItemScaleMax(q.question_type, q.scale_max, scaleMax);
            const itemSigma = scaleSigma(itemMax);
            const gapToBenchmark = scoreGapFromTarget(q.score, q.benchmark_score);
            return (
              <tr
                key={`${q.question_id}:${q.statement_key ?? 'scalar'}`}
                className="border-b border-[var(--color-border)] align-top"
              >
                <td className="py-2 pr-2 text-[var(--color-secondary-text)] tabular-nums">
                  {q.display_number}
                </td>
                <td className="py-2 pr-2 text-[var(--color-surface-text)]">{q.section_name}</td>
                <td className="py-2 pr-2 text-[var(--color-surface-text)]">
                  <span>{label}</span>
                  {q.statement_label?.trim() && (
                    <span className="mt-0.5 block text-xs text-[var(--color-secondary-text)]">
                      {q.question_text}
                    </span>
                  )}
                </td>
                <td className="py-2 pr-2 text-[var(--color-surface-text)]">{scoreDisplay}</td>
                {showBenchmark && (
                  <td className="py-2 pr-2 text-[var(--color-surface-text)]">
                    {formatScore(q.benchmark_score)}
                  </td>
                )}
                {showBenchmark && (
                  <td className="py-2">
                    <GapValue gap={gapToBenchmark} sigma={itemSigma} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {footnote && (
        <p className="mt-3 text-xs text-[var(--color-secondary-text)]">{footnote}</p>
      )}
    </div>
  );
};
